import { useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { ChevronLeft, RefreshCw, Star, X } from 'lucide-react'
import AiRatingStars from '@/components/feature/AiRatingStars'
import StatusGainBadge from '@/components/feature/StatusGainBadge'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { useAchievement, useResetAiAnalysis, useUpdateAchievement } from '@/hooks/useAchievements'
import { RATING_COLORS } from '@/lib/rating-colors'
import { sourceBadgeLabel } from '@/lib/source-badge'
import type { Achievement } from '@/types'


const schema = z.object({
  title: z.string().min(1, 'タイトルは必須です').max(255),
  score: z.string().optional(),
  rating: z.number().min(1).max(5),
  achieved_at: z.string().min(1),
  memo: z.string().optional(),
})

type FormValues = z.infer<typeof schema>

function toLocalDateString(isoString: string): string {
  return isoString.slice(0, 10)
}

// achievement が確定した状態で defaultValues を渡してフォームを初期化する
function EditForm({ achievement }: { achievement: Achievement }) {
  const navigate = useNavigate()
  const update = useUpdateAchievement(achievement.id)
  const resetAi = useResetAiAnalysis()

  const [tags, setTags] = useState<string[]>(achievement.tags)
  const [tagInput, setTagInput] = useState('')
  const [rating, setRating] = useState(achievement.rating)
  const sourceLabel = sourceBadgeLabel(achievement.source)

  const { register, handleSubmit, setValue, formState: { errors } } = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      title: achievement.title,
      score: achievement.score ?? '',
      rating: achievement.rating,
      achieved_at: toLocalDateString(achievement.achieved_at),
      memo: achievement.memo ?? '',
    },
  })

  const addTag = () => {
    const t = tagInput.trim()
    if (t && !tags.includes(t)) setTags([...tags, t])
    setTagInput('')
  }

  const onSubmit = (values: FormValues) => {
    update.mutate(
      {
        title: values.title,
        score: values.score ?? '',
        rating: values.rating,
        tags,
        memo: values.memo ?? '',
        achieved_at: `${values.achieved_at}T00:00:00.000Z`,
      },
      {
        onSuccess: () => {
          toast.success('保存しました')
          navigate(-1)
        },
        onError: () => toast.error('保存に失敗しました'),
      },
    )
  }

  return (
    <div className="py-4">
      <button
        type="button"
        onClick={() => navigate(-1)}
        className="flex items-center gap-1 text-sm text-muted-foreground mb-4 -ml-1 min-h-[44px] px-1"
      >
        <ChevronLeft size={18} />
        戻る
      </button>

      {sourceLabel && (
        <div className="mb-3">
          <span className="inline-flex items-center rounded-full border px-2.5 py-0.5 text-xs text-muted-foreground">
            {sourceLabel} で登録
          </span>
        </div>
      )}

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
        {/* タイトル */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium">タイトル *</label>
          <Input {...register('title')} placeholder="実績名を入力" autoComplete="off" />
          {errors.title && <p className="text-xs text-destructive">{errors.title.message}</p>}
        </div>

        {/* スコア */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium">スコア・結果（任意）</label>
          <Input {...register('score')} placeholder="例: 850点、1位、○○賞" />
        </div>

        {/* 評価 */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium">評価</label>
          <div className="flex gap-1">
            {[1, 2, 3, 4, 5].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => { setRating(n); setValue('rating', n) }}
                className="p-1"
              >
                <Star
                  size={24}
                  className={n <= rating ? RATING_COLORS.self : RATING_COLORS.empty}
                />
              </button>
            ))}
          </div>
        </div>

        {/* 日付 */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium">日付</label>
          <Input {...register('achieved_at')} type="date" />
        </div>

        {/* タグ */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium">タグ（任意）</label>
          <div className="flex gap-2">
            <Input
              value={tagInput}
              onChange={(e) => setTagInput(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addTag() } }}
              placeholder="タグを入力して Enter"
            />
            <Button type="button" variant="outline" onClick={addTag} size="sm">追加</Button>
          </div>
          {tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-1">
              {tags.map((tag) => (
                <span key={tag} className="flex items-center gap-1 text-xs bg-secondary text-secondary-foreground rounded-full px-2.5 py-1">
                  {tag}
                  <button type="button" onClick={() => setTags(tags.filter((t) => t !== tag))}>
                    <X size={12} />
                  </button>
                </span>
              ))}
            </div>
          )}
        </div>

        {/* AI タグ */}
        {achievement.is_ai_analyzed && (
          <div className="flex justify-end">
            <button
              type="button"
              onClick={() => resetAi.mutate(achievement.id, {
                onSuccess: () => toast.success('AI再判定フラグをリセットしました'),
                onError: () => toast.error('リセットに失敗しました'),
              })}
              disabled={resetAi.isPending}
              className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground min-h-[44px] px-1"
            >
              <RefreshCw size={12} />
              AI再判定
            </button>
          </div>
        )}
        {achievement.is_ai_analyzed && (
          <div className="space-y-1.5">
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium">AI タグ</label>
              {achievement.ai_rating !== null && (
                <div className="flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">AI評価</span>
                  <AiRatingStars rating={achievement.ai_rating} />
                </div>
              )}
            </div>
            {achievement.ai_tags.length > 0 ? (
              <div className="flex flex-wrap gap-1.5">
                {achievement.ai_tags.map((tag) => (
                  <span
                    key={tag}
                    className="flex items-center gap-1 text-xs bg-green-500/10 text-green-700 dark:text-green-400 rounded-full px-2.5 py-1"
                  >
                    {tag}
                    <button
                      type="button"
                      onClick={() => {
                        const next = achievement.ai_tags.filter((t) => t !== tag)
                        update.mutate(
                          { ai_tags: next },
                          { onError: () => toast.error('削除に失敗しました') },
                        )
                      }}
                    >
                      <X size={12} />
                    </button>
                  </span>
                ))}
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">AI タグはすべて削除されました</p>
            )}

            <StatusGainBadge
              statusGain={achievement.status_gain}
              earnedPoints={achievement.earned_points}
              isAiAnalyzed={achievement.is_ai_analyzed}
            />
          </div>
        )}

        {/* メモ */}
        <div className="space-y-1.5">
          <label className="text-sm font-medium">メモ（任意）</label>
          <Textarea {...register('memo')} placeholder="振り返りを自由に記入..." rows={3} />
        </div>

        <Button type="submit" className="w-full" disabled={update.isPending}>
          {update.isPending ? '保存中...' : '保存する'}
        </Button>
      </form>
    </div>
  )
}

export default function AchievementDetailPage() {
  const { id } = useParams<{ id: string }>()
  const { data: achievement, isLoading } = useAchievement(Number(id))

  if (isLoading) {
    return <div className="py-8 text-center text-muted-foreground text-sm">読み込み中...</div>
  }

  if (!achievement) {
    return <div className="py-8 text-center text-muted-foreground text-sm">実績が見つかりません</div>
  }

  return <EditForm achievement={achievement} />
}
